import { computed, defineComponent, PropType } from "@vue/composition-api"

export default defineComponent({
    name: 'JsonViewer',
    props: {
        data: {
            type: [Object, Array, String, Number, Boolean] as PropType<unknown>,
            default: null,
        },
        depth: {
            type: Number,
            default: 0,
        },
        label: {
            type: String,
            default: null,
        },
        maxDepth: {
            type: Number,
            default: 10,
        }
    },
    setup(props) {
        const dataType = computed(() => {
            if (props.data === null || props.data === undefined) return 'null';
            if (Array.isArray(props.data)) return 'array';
            return typeof props.data;
        });

        const isObject = computed(() => dataType.value === 'object');
        const isArray = computed(() => dataType.value === 'array');
        const isPrimitive = computed(() => !isObject.value && !isArray.value);

        const objectEntries = computed(() => {
            if (!isObject.value || !props.data) return [];
            return Object.entries(props.data as unknown as Record<string, unknown>)
                .filter(([key]) => !key.startsWith('$'));
        });

        const arrayItems = computed(() => {
            if (!isArray.value || !props.data) return [];
            return props.data as unknown as unknown[];
        });

        const formatPrimitiveValue = computed(() => {
            if (props.data === null || props.data === undefined) return 'N/A';
            if (props.data === '') return '(empty)';
            if (typeof props.data === 'boolean') return props.data ? 'Yes' : 'No';
            return String(props.data);
        });

        const formatLabel = (key: string): string => {
            // Convert camelCase or PascalCase to Title Case with spaces
            return key
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, str => str.toUpperCase())
                .replace(/_/g, ' ')
                .trim();
        };

        const isEmptyObject = computed(() => isObject.value && objectEntries.value.length === 0);
        const isEmptyArray = computed(() => isArray.value && arrayItems.value.length === 0);

        const shouldRecurse = computed(() => props.depth < props.maxDepth);

        const isComplexArrayItem = (item: unknown): boolean => {
            return item !== null && typeof item === 'object';
        };

        return {
            dataType,
            isObject,
            isArray,
            isPrimitive,
            objectEntries,
            arrayItems,
            formatPrimitiveValue,
            formatLabel,
            isEmptyObject,
            isEmptyArray,
            shouldRecurse,
            isComplexArrayItem,
        };
    }
})
